// The ProseMirror schema shared by the user document and note editors — the one place that decides
// which markdown a card can actually hold. It has to line up with RICH_MARKDOWN in overview.utils:
// the parser emits a node type, this schema must own it, or `setContent` throws the document away.
//
// StarterKit v3 already ships blockquote, codeBlock, code, strike, horizontalRule, hardBreak AND
// link — only task lists and images are missing, so those are the only two additions below.
import { Node, mergeAttributes } from '@tiptap/core'
import StarterKit from '@tiptap/starter-kit'
import { TaskItem, TaskList } from '@tiptap/extension-list'

/**
 * Inline `image` node — the schema slot `![alt](src)` parses into.
 *
 * Local rather than @tiptap/extension-image, which this app doesn't depend on; it's a dozen lines and
 * keeps the render under our control. INLINE on purpose: an inline node is legal anywhere text is, so
 * an image inside a list item, a quote or a link (the badge pattern) can never produce a document
 * ProseMirror refuses to build. CSS gives it a block box — see UserDoc/Note.module.css.
 */
export const ProseImage = Node.create({
  name: 'image',
  inline: true,
  group: 'inline',
  draggable: true,
  addAttributes() {
    return {
      src: { default: null },
      alt: { default: null },
      title: { default: null },
    }
  },
  parseHTML() {
    return [{ tag: 'img[src]' }]
  },
  renderHTML({ HTMLAttributes }) {
    // `loading=lazy` matters on the canvas: a pasted article can carry a dozen remote images, and
    // every card mounts its own editor — eager loading would fire them all on the first paint.
    return ['img', mergeAttributes(HTMLAttributes, { loading: 'lazy', draggable: 'false' })]
  },
})

// Deliberately un-annotated: StarterKit's Extension type doesn't satisfy `AnyExtension`, so both
// editors (and getSchema) cast at the use site, exactly as they did before this list existed.
export const richProseExtensions = [
  StarterKit.configure({
    // openOnClick stays FALSE on purpose: these editors render on the canvas, where a click is a
    // select/drag gesture. Navigating away mid-drag would tear the canvas down and lose the
    // debounced save. The link is preserved, styled and copyable — just not a navigation trigger.
    link: {
      openOnClick: false,
      autolink: true,
      HTMLAttributes: { rel: 'noopener noreferrer nofollow', target: '_blank' },
    },
  }),
  TaskList,
  TaskItem.configure({ nested: true }),
  ProseImage,
]
